import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { FileCheck2, ShieldCheck, Loader2, AlertTriangle, Download, CheckCircle2, ChevronDown } from "lucide-react";
import { api, API, errMsg } from "../../lib/api";
import { dateBR, maskCpf } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { DemoBadge } from "../../components/common";
import { SignaturePad } from "../../components/SignaturePad";

function DocCard({ token, d, i, signed, done, accepted, onAccept }) {
  const [open, setOpen] = useState(i === 0 && !signed);
  return (
    <div className={`bg-white border rounded-2xl ${accepted ? "border-emerald-300" : ""}`} data-testid={`sign-doc-${i}`}>
      <button className="w-full flex items-center gap-3 px-4 py-4 text-left" onClick={() => setOpen(!open)} data-testid={`sign-doc-toggle-${i}`}>
        {accepted || signed ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <span className="h-5 w-5 rounded-full border-2 border-slate-300 text-[10px] flex items-center justify-center text-slate-500 shrink-0">{i + 1}</span>}
        <span className="flex-1 font-medium text-sm">{d.title}</span><ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-800 pr-1 border-t pt-3">{d.body}</div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-slate-400 font-mono truncate">SHA-256: {d.original_hash.slice(0, 24)}…</span>
            <a href={`${API}/public/contract/${token}/pdf/${d.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-700 font-medium shrink-0" data-testid={`sign-download-pdf-${i}`}><Download className="h-3.5 w-3.5" />{done ? "PDF assinado" : "Baixar PDF"}</a>
          </div>
        </div>)}
      {!signed && (
        <label className="flex items-start gap-2 text-sm border-t px-4 py-3 bg-slate-50/60 rounded-b-2xl"><Checkbox checked={accepted} onCheckedChange={(v) => onAccept(!!v)} className="mt-0.5" data-testid={`sign-accept-${i}`} />Li e concordo com {d.title}</label>)}
    </div>
  );
}

function SignBox({ token, c, accepted, onSigned }) {
  const [cpf, setCpf] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const all = accepted.length === c.docs.length;
  const sign = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/public/contract/${token}/sign`, { cpf, signature: sig, accepted }); onSigned(data); toast.success("Assinatura concluída!"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border rounded-2xl p-5 space-y-4" data-testid="sign-box">
      <h2 className="text-lg font-semibold">Assinar {c.docs.length > 1 ? `os ${c.docs.length} documentos` : ""}</h2>
      <p className={`text-xs ${all ? "text-emerald-700" : "text-amber-700"}`} data-testid="sign-accept-progress">{accepted.length} de {c.docs.length} documento(s) aceito(s){all ? "" : ". Marque \"Li e concordo\" em cada um."}</p>
      <div><Label>Confirme seu CPF</Label><Input className="mt-1.5 h-12 text-base" inputMode="numeric" placeholder="000.000.000-00" value={cpf} onChange={(e) => setCpf(maskCpf(e.target.value))} data-testid="sign-cpf-input" />
        <p className="text-[11px] text-slate-500 mt-1">O CPF cadastrado termina em {c.cpf_hint?.slice(-2)}.</p></div>
      <SignaturePad onChange={setSig} testid="candidate-signature-pad" />
      <Button className="w-full h-12 text-base" disabled={!sig || !all || cpf.replace(/\D/g, "").length !== 11 || busy} onClick={sign} data-testid="sign-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assinar"}</Button>
      <p className="flex items-center gap-1.5 text-[11px] text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />Uma única assinatura vale para todos os documentos aceitos. Registramos data, hora, IP e dispositivo como prova.</p>
    </div>
  );
}

export default function SignContract() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [accepted, setAccepted] = useState([]);
  useEffect(() => { api.get(`/public/contract/${token}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  if (err) return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50"><div className="max-w-sm text-center bg-white border rounded-2xl p-6" data-testid="sign-error"><AlertTriangle className="h-10 w-10 text-amber-600 mx-auto" /><p className="mt-3 text-sm text-slate-700">{err}</p></div></div>;
  if (!c) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const signed = c.status !== "aguardando_candidato";
  const accept = (id, v) => setAccepted((a) => (v ? [...new Set([...a, id])] : a.filter((x) => x !== id)));
  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="sign-contract-page">
      <header className="bg-slate-900 text-white px-5 pt-6 pb-10 rounded-b-3xl relative overflow-hidden">
        <div className="absolute inset-0 hero-grain" />
        <div className="relative max-w-lg mx-auto">
          <img src="/logo-white.png" alt="DPdigi" className="h-8 w-auto" />
          <div className="mt-6 flex items-center gap-2 text-xs text-sky-200">{c.company}{c.demo && <DemoBadge />}</div>
          <h1 className="text-2xl font-bold mt-1" data-testid="sign-title">{c.title}</h1>
          <p className="text-sm text-slate-300 mt-1">Para {c.candidate}{!signed && ` · link válido até ${dateBR(c.expires_at)}`}</p>
        </div>
      </header>
      <main className="max-w-lg mx-auto px-4 -mt-5 relative space-y-3">
        {signed && <div className="bg-white border rounded-2xl p-6 text-center" data-testid="sign-done"><FileCheck2 className="h-12 w-12 text-emerald-600 mx-auto" /><h2 className="text-xl font-semibold mt-3">Assinatura concluída!</h2>
          <p className="text-sm text-slate-500 mt-1">Você assinou em {c.signed_at_label}. {c.status === "concluido" ? "A empresa também já assinou." : "Agora falta a assinatura da empresa; você não precisa fazer mais nada."}</p></div>}
        {!signed && c.docs.length > 1 && <p className="text-xs text-slate-600 px-1 pt-2">Leia os {c.docs.length} documentos abaixo, marque "Li e concordo" em cada um e assine uma única vez no final.</p>}
        {c.docs.map((d, i) => <DocCard key={d.id} token={token} d={d} i={i} signed={signed} done={c.status === "concluido"} accepted={accepted.includes(d.id)} onAccept={(v) => accept(d.id, v)} />)}
        {!signed && <SignBox token={token} c={c} accepted={accepted} onSigned={setC} />}
      </main>
    </div>
  );
}
